import React, { useState, useEffect } from 'react';
import api from '../api';
import { PLATFORM_META } from '../platforms';

const PLATFORM_KEYS = ['youtube', 'twitch', 'facebook', 'tiktok', 'instagram', 'kick', 'custom'];

export default function DestinationModal({ destination, type, onSave, onClose }) {
  const isEdit = !!destination;
  const isGlobal = type === 'global';
  const [platform, setPlatform] = useState(destination?.platform || 'youtube');
  const [form, setForm] = useState({
    label:      destination?.label || '',
    rtmp_url:   destination?.rtmp_url || PLATFORM_META.youtube.rtmpUrl,
    stream_key: destination?.stream_key || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      const pm = PLATFORM_META[platform];
      setForm(f => ({ ...f, rtmp_url: pm.rtmpUrl, label: f.label || pm.label }));
    }
  }, [platform, isEdit]);

  const handleSave = async () => {
    if (!form.label.trim()) { setError('Label required'); return; }
    if (!form.rtmp_url.trim()) { setError('RTMP URL required'); return; }
    if (!form.stream_key.trim()) { setError('Stream key required'); return; }
    setError(''); setLoading(true);
    try {
      const endpoint = isGlobal ? '/stream/global-destinations' : '/stream/destinations';
      if (isEdit) {
        await api.patch(`${endpoint}/${destination.id}`, { ...form, platform });
      } else {
        await api.post(endpoint, { ...form, platform });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const pm = PLATFORM_META[platform];

  const s = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '24px' },
    modal: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '32px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
    header: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' },
    title: { fontSize: '19px', fontWeight: '800' },
    typeBadge: { fontSize: '10px', fontWeight: '800', padding: '3px 7px', borderRadius: '4px', background: isGlobal ? 'rgba(0,230,118,0.1)' : 'rgba(145,70,255,0.1)', color: isGlobal ? 'var(--signal)' : '#9146ff', border: `1px solid ${isGlobal ? 'rgba(0,230,118,0.2)' : 'rgba(145,70,255,0.2)'}` },
    field: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '7px' },
    hint: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '5px', lineHeight: '1.5' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '7px', marginBottom: '18px' },
    platBtn: (sel, color) => ({ padding: '9px 6px', borderRadius: '8px', cursor: 'pointer', textAlign: 'center', border: `1px solid ${sel ? color : 'var(--border)'}`, background: sel ? `${color}18` : 'var(--surface2)', color: sel ? color : 'var(--text-dim)', fontWeight: sel ? '700' : '500', fontSize: '12px', transition: 'all 0.15s' }),
    platDot: (color) => ({ width: '6px', height: '6px', borderRadius: '50%', background: color, display: 'inline-block', marginRight: '5px' }),
    tlsBadge: { display: 'inline-block', fontSize: '10px', fontWeight: '700', padding: '2px 5px', borderRadius: '4px', background: 'rgba(0,230,118,0.1)', color: 'var(--signal)', border: '1px solid rgba(0,230,118,0.2)', marginLeft: '7px', verticalAlign: 'middle' },
    row: { display: 'flex', gap: '10px', marginTop: '8px' },
    saveBtn: { flex: 1, padding: '12px', background: 'var(--signal)', color: '#000', fontWeight: '700', fontSize: '14px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer' },
    cancelBtn: { flex: 1, padding: '12px', background: 'var(--surface2)', color: 'var(--text)', fontWeight: '600', fontSize: '14px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer' },
    error: { background: 'var(--danger-bg)', border: '1px solid rgba(255,77,77,0.25)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: '13px', marginBottom: '14px' },
    globalNote: { background: 'rgba(0,230,118,0.05)', border: '1px solid rgba(0,230,118,0.15)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: '1.6' },
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <div style={s.title}>{isEdit ? 'Edit' : 'Add'} destination</div>
          <span style={s.typeBadge}>{isGlobal ? 'GLOBAL' : 'PERSONAL'}</span>
        </div>

        {isGlobal && !isEdit && (
          <div style={s.globalNote}>
            🌐 <strong>Global destination</strong> — all users will stream to this platform when they go live.
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <div style={s.field}>
          <label style={s.label}>Platform</label>
          <div style={s.grid}>
            {PLATFORM_KEYS.map(key => {
              const meta = PLATFORM_META[key];
              return (
                <button key={key} style={s.platBtn(platform === key, meta.color)} onClick={() => setPlatform(key)}>
                  <span style={s.platDot(meta.color)} />{meta.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={s.field}>
          <label style={s.label}>Label</label>
          <input placeholder={`e.g. Dennery FM ${pm.label}`} value={form.label}
            onChange={e => setForm({...form, label: e.target.value})} />
        </div>

        <div style={s.field}>
          <label style={s.label}>
            RTMP Server URL
            {pm.usesTLS && <span style={s.tlsBadge}>RTMPS / TLS</span>}
          </label>
          <input placeholder="rtmp://..." value={form.rtmp_url}
            onChange={e => setForm({...form, rtmp_url: e.target.value})}
            disabled={platform !== 'custom'}
            style={platform !== 'custom' ? { opacity: 0.6 } : {}} />
          {platform !== 'custom' && (
            <div style={s.hint}>Auto-filled for {pm.label}{pm.usesTLS ? ' · Encrypted connection handled automatically' : ''}</div>
          )}
        </div>

        <div style={s.field}>
          <label style={s.label}>Stream Key</label>
          <input placeholder="Paste your stream key" value={form.stream_key} type="password"
            onChange={e => setForm({...form, stream_key: e.target.value})} />
          <div style={s.hint}>📍 {pm.keyHint}</div>
        </div>

        <div style={s.row}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={s.saveBtn} onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : isEdit ? 'Save changes' : 'Add destination'}
          </button>
        </div>
      </div>
    </div>
  );
}
